/**
 * FocalCard — secondary content panel.
 *
 * Two routing strategies cover all 16 FocalState kinds (v0.5.3 AE-039):
 *
 *  A. INLINE OVERLAY — kinds that need a modal-shaped view:
 *     chart, order, agent, backtest.
 *
 *  B. TAB NAVIGATION — kinds that already have a primary surface in the
 *     5-tab IA. FocalCard navigates to the matching route (carrying a
 *     query string when relevant) and clears focal state. Examples:
 *     positions -> /portfolio, playbook -> /settings#playbook,
 *     research?query -> /research?q=..., thesis_wizard -> /research?wizard=...
 *
 * Before v0.5.3 only the four overlay kinds rendered; the other 12 fell
 * through silently. The audit found this regression — users could click
 * "Show me your positions" in chat and nothing would happen.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../lib/api';
import { useFocal, type FocalState } from '../lib/focal';
import { OrderTicket } from './OrderTicket';
import { AgentInspector } from './AgentInspector';
import { BacktestPanel } from './BacktestPanel';

// Kinds that render as an inline overlay (do NOT navigate). v0.5.4 AE-066:
// `strategies` now opens the backtest overlay too since BacktestPanel IS
// the strategies surface — there was no `/research?view=strategies` page.
const OVERLAY_KINDS = new Set<FocalState['kind']>([
  'chart',
  'order',
  'agent',
  'backtest',
  'strategies',
  'thesis_detail',
]);

// Map every non-overlay kind to a tab route (and optional query). The
// destination matches the App.tsx 5-tab IA. v0.5.4 AE-066/AE-068: Settings
// destinations use ?tab= / ?sub= query strings (URL state) instead of
// #hash anchors which SettingsPanel never honored.
function focalToRoute(s: FocalState): string | null {
  switch (s.kind) {
    case 'positions':
      return '/portfolio';
    case 'portfolio_board':
      return '/portfolio?view=board';
    case 'theses':
      return '/portfolio?view=theses';
    case 'orders':
      return '/approvals';
    case 'pending':
      return '/approvals';
    case 'morning_brief':
      return '/today';
    case 'research':
      return s.query ? `/research?q=${encodeURIComponent(s.query)}` : '/research';
    case 'thesis_wizard':
      return s.wizardId
        ? `/research?wizard=${encodeURIComponent(s.wizardId)}`
        : '/research?wizard=new';
    case 'playbook':
      return '/settings?tab=guardrails&sub=playbook';
    case 'autonomy':
      return '/settings?tab=guardrails&sub=autonomy';
    case 'profile':
      return '/settings?tab=profile';
    case 'settings':
      return '/settings';
    default:
      return null;
  }
}

export function FocalCard() {
  const { state, set } = useFocal();
  const navigate = useNavigate();

  // Navigation effect runs whenever a non-overlay focal state is set.
  // Cleared from the URL bar's perspective by also clearing focal state
  // so a back-button trip doesn't re-pop the same destination.
  useEffect(() => {
    if (!state) return;
    if (OVERLAY_KINDS.has(state.kind)) return;
    const route = focalToRoute(state);
    if (route) {
      // navigate first, THEN clear — clearing first would cancel the
      // re-render that mounts the destination page.
      navigate(route);
    }
    // Either way, the focal state has been consumed.
    set(null);
  }, [state, navigate, set]);

  if (!state) return null;
  if (!OVERLAY_KINDS.has(state.kind)) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={() => set(null)}
      data-testid="focal-overlay"
    >
      <div
        className="card p-4 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-sm">{focalTitle(state)}</div>
          <button onClick={() => set(null)} className="btn text-xs">
            close
          </button>
        </div>
        {state.kind === 'chart' && <ChartView symbol={state.symbol} />}
        {state.kind === 'order' && <OrderTicket symbol={state.symbol} />}
        {state.kind === 'agent' && <AgentInspector pane_id={state.pane_id} />}
        {state.kind === 'backtest' && <BacktestPanel />}
        {state.kind === 'strategies' && <BacktestPanel />}
        {state.kind === 'thesis_detail' && <ThesisDetailView thesisId={state.thesisId} />}
      </div>
    </div>
  );
}

function focalTitle(s: FocalState): string {
  switch (s.kind) {
    case 'chart':
      return `${s.symbol} — chart`;
    case 'order':
      return `Order ticket — ${s.symbol}`;
    case 'agent':
      return `Agent — ${s.pane_id}`;
    case 'backtest':
      return 'Backtest';
    case 'strategies':
      return 'Strategies — compare';
    case 'thesis_detail':
      return `Thesis ${s.thesisId.slice(0, 8)}`;
    default:
      return '';
  }
}

function ThesisDetailView({ thesisId }: { thesisId: string }) {
  const [detail, setDetail] = useState<api.ThesisDetail | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const navigate = useNavigate();
  const { set } = useFocal();

  useEffect(() => {
    setLoadState('loading');
    setErrMsg(null);
    api.engineer
      .getThesis(thesisId)
      .then((d) => {
        setDetail(d);
        setLoadState('ready');
      })
      .catch((e: unknown) => {
        setLoadState('error');
        setErrMsg(e instanceof Error ? e.message : String(e));
      });
  }, [thesisId]);

  if (loadState === 'loading') {
    return <div className="text-xs text-ink-mute" data-testid="thesis-detail-loading">loading thesis…</div>;
  }
  if (loadState === 'error' || !detail) {
    return (
      <div className="text-xs text-bad" data-testid="thesis-detail-error">
        Couldn&apos;t load thesis {thesisId.slice(0, 8)}. {errMsg ? <span className="text-ink-mute">({errMsg})</span> : null}
      </div>
    );
  }

  const fmtUsd = (v: number | null | undefined) =>
    v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const fmtPct = (v: number | null | undefined) =>
    v == null ? '—' : `${(Number(v) * 100).toFixed(0)}%`;

  return (
    <div className="space-y-3 text-sm" data-testid="thesis-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-base font-semibold">{detail.symbol ?? detail.positions[0]?.symbol ?? 'Thesis'}</span>
        <span className={`pill text-[10px] ${detail.status === 'open' ? 'text-ok' : 'text-ink-mute'}`}>
          {detail.status}
        </span>
        <span className="pill text-[10px] text-ink-mute">{detail.archetype}</span>
        <span className="pill text-[10px] text-ink-mute">{detail.thesis_type}</span>
      </div>

      <div className="text-[11px] text-ink-mute flex flex-wrap gap-x-4">
        <span>P(success) <span className="num text-ink">{fmtPct(detail.p_success)}</span></span>
        <span>q(failure) <span className="num text-ink">{fmtPct(detail.q_failure)}</span></span>
        <span>conviction <span className="num text-ink">{detail.conviction}/10</span></span>
        <span>horizon <span className="num text-ink">{detail.horizon_days}d</span></span>
        {detail.target != null && <span>target <span className="num text-ink">${detail.target}</span></span>}
        {detail.stop != null && <span>stop <span className="num text-ink">${detail.stop}</span></span>}
      </div>

      {detail.statement_md && (
        <div className="card p-2">
          <div className="text-[11px] text-ink-mute mb-1">Statement</div>
          <div className="whitespace-pre-wrap text-[12px]">{detail.statement_md}</div>
        </div>
      )}

      {detail.contrarian_review_md && (
        <div className="card p-2 border-warn/40">
          <div className="text-[11px] text-warn mb-1">Contrarian review</div>
          <div className="whitespace-pre-wrap text-[12px]">{detail.contrarian_review_md}</div>
        </div>
      )}

      {detail.invalidation_trigger && (
        <div className="text-[11px] text-ink-mute">
          <span className="font-semibold text-ink">Invalidation:</span> {detail.invalidation_trigger}
        </div>
      )}

      <div className="card p-2">
        <div className="text-[11px] text-ink-mute mb-1">Attribution</div>
        <div className="flex gap-3 text-[12px]">
          <span>Total <span className="num font-semibold">{fmtUsd(detail.attribution.total_value)}</span></span>
          <span>LIVE <span className="num">{fmtUsd(detail.attribution.live_value)}</span></span>
          <span>SIM <span className="num">{fmtUsd(detail.attribution.sim_value)}</span></span>
        </div>
        {detail.attribution.rows.length > 0 && (
          <table className="w-full text-[11px] mt-2">
            <thead className="text-ink-mute">
              <tr>
                <th className="text-left">Sym</th>
                <th className="text-left">Mode</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {detail.attribution.rows.map((r, i) => (
                <tr key={i} className="border-t border-edge">
                  <td>{r.symbol}</td>
                  <td className="uppercase text-[10px]">{r.mode}</td>
                  <td className="text-right num">{r.attributed_qty.toFixed(2)}</td>
                  <td className="text-right num">{fmtUsd(r.attributed_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card p-2">
        <div className="text-[11px] text-ink-mute mb-1">Positions ({detail.positions.length})</div>
        <table className="w-full text-[11px]">
          <thead className="text-ink-mute">
            <tr>
              <th className="text-left">Sym</th>
              <th className="text-right">Target %</th>
              <th className="text-right">Target Qty</th>
            </tr>
          </thead>
          <tbody>
            {detail.positions.map((p, i) => (
              <tr key={i} className="border-t border-edge">
                <td className="font-semibold">
                  <button
                    className="underline hover:opacity-80"
                    onClick={() => { set(null); navigate(`/research?q=${encodeURIComponent(p.symbol)}`); }}
                  >
                    {p.symbol}
                  </button>
                </td>
                <td className="text-right num">{p.target_weight_pct.toFixed(0)}%</td>
                <td className="text-right num">{p.target_qty == null ? '—' : p.target_qty.toFixed(0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartView({ symbol }: { symbol: string }) {
  const [bars, setBars] = useState<api.Bar[]>([]);
  const [period, setPeriod] = useState('1m');
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    setLoadState('loading');
    setErrMsg(null);
    api.market
      .history(symbol, period, '1d')
      .then((r) => {
        setBars(r.bars);
        setLoadState('ready');
      })
      .catch((e: unknown) => {
        setBars([]);
        setLoadState('error');
        const msg = e instanceof Error ? e.message : String(e);
        setErrMsg(msg);
      });
  }, [symbol, period]);

  if (loadState === 'loading') {
    return <div className="text-xs text-ink-mute" data-testid="chart-loading">loading history...</div>;
  }
  if (loadState === 'error') {
    return (
      <div className="text-xs text-bad" data-testid="chart-error">
        Couldn&apos;t load chart history for {symbol}. {errMsg ? <span className="text-ink-mute">({errMsg})</span> : null}
      </div>
    );
  }
  if (bars.length === 0) {
    return <div className="text-xs text-ink-mute" data-testid="chart-empty">No bars returned for {symbol} ({period}).</div>;
  }
  const closes = bars.map((b) => b.close);
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const range = Math.max(1e-6, max - min);
  const w = 720;
  const h = 220;
  const path = closes
    .map((c, i) => {
      const x = (i / Math.max(1, closes.length - 1)) * w;
      const y = h - ((c - min) / range) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  const last = closes[closes.length - 1];
  const first = closes[0];
  const totalPct = first > 0 ? ((last - first) / first) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-2xl font-mono">
          ${last.toFixed(2)}{' '}
          <span className={totalPct >= 0 ? 'text-ok text-sm' : 'text-bad text-sm'}>
            {totalPct >= 0 ? '+' : ''}{totalPct.toFixed(2)}%
          </span>
        </div>
        <div className="flex gap-1">
          {['5d', '1m', '3m', 'ytd', '1y'].map((p) => (
            <button
              key={p}
              className={`btn text-xs ${p === period ? 'border-accent text-accent' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-56 block" preserveAspectRatio="none">
        <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-accent" />
      </svg>
      <div className="text-[11px] text-ink-mute mt-1">
        {bars[0].ts.split('T')[0]} — {bars[bars.length - 1].ts.split('T')[0]} · {bars.length} bars
      </div>
    </div>
  );
}
